/**
 * Simplified Streaming Service for IVS Integration
 * Handles core streaming operations with AWS best practices
 */

const DEBUG_STREAM = String(process.env.REACT_APP_DEBUG_STREAM || '').toLowerCase() === 'true';

class StreamingService {
    constructor(baseUrl) {
        this.baseUrl = baseUrl;
        // Derive API root by stripping trailing '/streaming'
        this.apiRoot = typeof baseUrl === 'string' ? baseUrl.replace(/\/?streaming\/?$/, '') : baseUrl;
    }

    /**
     * Check stream status
     */
    async getStatus(type) {
        const url = new URL(`${this.baseUrl}/stream/status`, window.location.origin);
        if (type) url.searchParams.set('type', type);
        const response = await fetch(url.toString());
        if (!response.ok) {
            throw new Error(`Status check failed: ${response.status}`);
        }
        return await response.json();
    }

    /**
     * Start streaming
     */
    async startStream(type = 'match', options = {}) {
        const params = new URLSearchParams({
            type,
            force: options.force ? '1' : '0',
            wait_for_live: options.waitForLive ? '1' : '0',
            debug: options.debug ? '1' : '0'
        });
        // Optional source selectors: sourceUrl or bucket/key
        if (options.sourceUrl) params.set('source_url', options.sourceUrl);
        if (options.bucket) params.set('bucket', options.bucket);
        if (options.key) params.set('key', options.key);

        const response = await fetch(`${this.baseUrl}/stream/start?${params}`);

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Start stream failed (${response.status}): ${errorText}`);
        }

        return await response.json();
    }

    /**
     * Stop streaming
     */
    async stopStream(type, streamSessionId) {
        const url = new URL(`${this.baseUrl}/stream/stop`, window.location.origin);
        if (type) url.searchParams.set('type', String(type));
        if (streamSessionId) url.searchParams.set('stream_session_id', String(streamSessionId));
        
        const response = await fetch(url.toString(), {
            method: 'POST'
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Stop stream failed (${response.status}): ${errorText}`);
        }

        return await response.json();
    }

    /**
     * Get playback URL
     */
    async getPlaybackUrl(type) {
        const url = new URL(`${this.baseUrl}/playback/url`, window.location.origin);
        if (type) url.searchParams.set('type', type);
        const response = await fetch(url.toString());

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Get playback URL failed (${response.status}): ${errorText}`);
        }

        return await response.json();
    }

    /**
     * Get stream health summary
     */
    async getHealth(type) {
        const url = new URL(`${this.baseUrl}/stream/health`, window.location.origin);
        if (type) url.searchParams.set('type', type);
        const response = await fetch(url.toString());
        if (!response.ok) {
            throw new Error(`Health check failed: ${response.status}`);
        }
        return await response.json();
    }

    /**
     * Get stream quality metrics
     */
    async getQuality(type) {
        const url = new URL(`${this.baseUrl}/stream/quality`, window.location.origin);
        if (type) url.searchParams.set('type', type);
        const response = await fetch(url.toString());
        if (!response.ok) {
            throw new Error(`Quality fetch failed: ${response.status}`);
        }
        return await response.json();
    }

    /**
     * Get ffmpeg log tail (requires debug start)
     */
    async getLogTail(tail = 2000, type) {
        const url = new URL(`${this.baseUrl}/stream/log`, window.location.origin);
        url.searchParams.set('tail', String(tail));
        if (type) url.searchParams.set('type', type);
        const response = await fetch(url.toString());
        if (!response.ok) {
            const text = await response.text();
            throw new Error(`Log fetch failed (${response.status}): ${text}`);
        }
        return await response.json();
    }

    /**
     * Ask backend for optimization suggestions
     */
    async optimize() {
        const response = await fetch(`${this.baseUrl}/stream/optimize`, { method: 'POST' });
        if (!response.ok) {
            const text = await response.text();
            throw new Error(`Optimize failed (${response.status}): ${text}`);
        }
        return await response.json();
    }

    /**
     * Wait for stream to go live
     */
    async waitForLive(timeout = 30000, pollInterval = 2000, type, abortRef) {
        const startTime = Date.now();

        while (Date.now() - startTime < timeout) {
            if (abortRef && abortRef.current) {
                throw new Error('aborted');
            }
            try {
                const status = await this.getStatus(type);

                const st = String(status?.status || '').toLowerCase();
                const isLive = st === 'live' || st === 'streaming' || status?.ivs_state === 'LIVE';
                const runningOk = typeof status?.running === 'boolean' ? status.running : true;
                if (isLive && runningOk) return status;

                await new Promise(resolve => setTimeout(resolve, pollInterval));
            } catch (error) {
                if (DEBUG_STREAM) console.warn('Wait for live error:', error);
                await new Promise(resolve => setTimeout(resolve, pollInterval));
            }
        }

        throw new Error('Stream did not go live within timeout period');
    }

    /**
     * Record a detection payload
     * @param {string} streamSessionId - Stream session identifier (e.g., st-...)
     * @param {object} metadata - Arbitrary metadata JSON to store
     */
    async postDetection(streamSessionId, metadata) {
        if (!streamSessionId) throw new Error('streamSessionId is required');
        const url = `${this.apiRoot}/detections`;
        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', accept: 'application/json' },
            body: JSON.stringify({ stream_id: streamSessionId, metadata })
        });
        if (!response.ok) {
            const text = await response.text().catch(() => '');
            throw new Error(`Post detection failed (${response.status}): ${text}`);
        }
        return await response.json().catch(() => ({}));
    }
}

export default StreamingService;
